import Seo, { pageSeo } from "../components/Seo";
import React from "react";
import { Link } from "react-router-dom";
import { Quote, BookOpen } from "lucide-react";
import HeroSlider from "../components/HeroSlider";
import ContactForm from "../components/ContactForm";
import CatalogueButtons from "../components/CatalogueButtons";
import { Reveal, SectionTitle, OutlineButton, LucideIcon } from "../components/Shared";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { products, applications, industries, stats, testimonials, faqs, company, facilityPhotos, shadeGroups } from "../mock";

const Home = () => {
  return (
    <div data-testid="home-page">
    <Seo {...pageSeo.home} />
      <HeroSlider />

      {/* Business & Products */}
      <section className="section bg-white" id="products">
        <div className="container-dic">
          <Reveal><SectionTitle>Our Inks &amp; Coatings</SectionTitle></Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p, i) => (
              <Reveal key={p.id} delay={i * 80}>
                <Link to={`/production#${p.id}`} className="product-card" data-testid={`product-card-${p.id}`}>
                  <div className="product-card__media">
                    <img src={p.image} alt={p.title} loading="lazy" />
                  </div>
                  <div className="product-card__body">
                    <h6 className="product-card__title">{p.title}</h6>
                    <p className="product-card__text">{p.description}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-10">
            <OutlineButton to="/production" data-testid="products-view-all">View All</OutlineButton>
          </div>
        </div>
      </section>

      {/* Catalogue CTA */}
      <section className="section-sm bg-[#8cc63f]" id="catalogue" data-testid="catalogue-band">
        <div className="container-dic flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <span className="hidden sm:flex w-14 h-14 rounded-full bg-white/20 text-white items-center justify-center shrink-0"><BookOpen size={26} /></span>
            <div>
              <h3 className="text-white text-[24px] md:text-[26px] font-bold leading-tight">Product Catalogue - Inks, Coatings &amp; Shade Card</h3>
              <p className="text-white/90 mt-1 text-[15px]">A 6-page PDF you can download, print or forward to your purchase team on WhatsApp.</p>
            </div>
          </div>
          <CatalogueButtons variant="white" />
        </div>
      </section>

      {/* Intro copy */}
      <section className="section-sm bg-white border-t border-[#f0f0f0]">
        <div className="container-dic">
          <Reveal className="max-w-4xl mx-auto text-center">
            <h2 className="section-title">{company.name} - Colour Partners to Print &amp; Industry</h2>
            <p className="mt-5 text-[15px] leading-7 text-[#58595b] text-justify md:text-center">
              {company.name} is a Telangana-based manufacturer of <Link to="/production" className="text-[#7ab929] underline-offset-2 hover:underline">printing inks and industrial coatings</Link>.
              From our production house at Medchal we formulate offset, flexographic and gravure inks, overprint varnishes and protective coatings
              for packaging, publishing, commercial printing and engineering customers. Every batch is colour-matched in our lab, tested for
              performance and packed in our delivery unit - and with ready stock on hand, we welcome bulk as well as emergency orders.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 80} className="stat">
                <div className="stat__value">{s.value}</div>
                <div className="stat__label">{s.label}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Applications - lime gradient with network texture */}
      <section className="section applications-bg" id="applications">
        <div className="container-dic relative z-10">
          <Reveal><SectionTitle>Where Our Inks Perform</SectionTitle></Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {applications.map((a, i) => (
              <Reveal key={a.id} delay={i * 60}>
                <Link to="/production" className="app-tile" data-testid={`application-tile-${a.id}`}>
                  <img src={a.image} alt={a.title} loading="lazy" />
                  <span className="app-tile__title">{a.title}</span>
                </Link>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-10">
            <OutlineButton to="/production" className="btn-outline--white-bg" data-testid="applications-view-all">See Production Details</OutlineButton>
          </div>
        </div>
      </section>

      {/* Shade card teaser */}
      <section className="section bg-white" id="shade-teaser">
        <div className="container-dic">
          <Reveal><SectionTitle sub="Browse our standard shade card and request a quote on any colour in one click.">Colours Ready to Ship</SectionTitle></Reveal>
          <Reveal><div className="shade-strip" data-testid="shade-strip">
            {shadeGroups.flatMap((g) => g.shades.filter((sh) => !sh.hex.startsWith("linear")).slice(0, g.id === "coatings" ? 6 : 5)).map((sh) => (
              <Link
                key={sh.code}
                to={{ pathname: "/contact", search: `?shade=${encodeURIComponent(`${sh.code} ${sh.name}`)}`, hash: "#quote" }}
                className="shade-strip__chip"
                style={{ backgroundColor: sh.hex }}
                title={`${sh.code} ${sh.name} - request a quote`}
                aria-label={`${sh.name} - request a quote`}
              >
                <span>{sh.name}</span>
              </Link>
            ))}
          </div></Reveal>
          <div className="text-center mt-10">
            <OutlineButton to="/production#shades" data-testid="shade-view-all">View Full Shade Card</OutlineButton>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="section bg-[#f7f7f7]" id="industries">
        <div className="container-dic">
          <Reveal><SectionTitle>Industries We Serve</SectionTitle></Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {industries.map((ind, i) => (
              <Reveal key={ind.id} delay={i * 60} className="text-center">
                <div className="industry-icon">
                  <LucideIcon name={ind.icon} size={26} />
                </div>
                <h6 className="mt-5 text-[16px] font-bold text-[#231f20]">{ind.title}</h6>
                <p className="mt-2 text-[13.5px] leading-6 text-[#58595b] text-justify">{ind.text}</p>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-12">
            <OutlineButton to="/contact" data-testid="industries-connect">Connect With Us</OutlineButton>
          </div>
        </div>
      </section>

      {/* Beyond inks - production & stock teaser */}
      <section className="section bg-white">
        <div className="container-dic">
          <Reveal><SectionTitle>Coatings, Ready Stock &amp; Reliable Delivery</SectionTitle></Reveal>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <Reveal className="grid grid-cols-2 gap-4">
              <img src={facilityPhotos.productionHouse.src} alt={facilityPhotos.productionHouse.alt} className="rounded-md shadow-md h-56 w-full object-cover col-span-2" loading="lazy" />
              <img src={facilityPhotos.deliveryUnit.src} alt={facilityPhotos.deliveryUnit.alt} className="rounded-md shadow-md h-44 w-full object-cover" loading="lazy" />
              <img src={facilityPhotos.stock2.src} alt={facilityPhotos.stock2.alt} className="rounded-md shadow-md h-44 w-full object-cover" loading="lazy" />
            </Reveal>
            <Reveal delay={120} className="space-y-5 text-[15px] leading-7 text-[#58595b] text-justify">
              <p>
                Besides printing inks, {company.shortName} supplies <Link to="/production#industrial-coatings" className="text-[#7ab929] hover:underline">industrial and packaging coatings</Link> -
                overprint varnishes, barrier coatings and protective finishes that keep printed and fabricated surfaces looking new for longer.
              </p>
              <p>
                Our production house is equipped with high-speed dispersers and mills operated by an experienced team, while our
                <Link to="/production#delivery" className="text-[#7ab929] hover:underline"> delivery unit</Link> packs, labels and stages every order for rapid dispatch.
              </p>
              <p>
                With a well-maintained <Link to="/production#stock" className="text-[#7ab929] hover:underline">stock yard</Link>, we are always ready to accept bulk orders and emergency requirements -
                because your press should never stop for want of ink.
              </p>
              <Link to="/production" className="btn-green" data-testid="beyond-explore">Explore Production</Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section bg-[#f7f7f7]">
        <div className="container-dic">
          <Reveal><SectionTitle sub="Illustrative feedback shown as sample text - replace with your customers' own words.">What Our Customers Value</SectionTitle></Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((t, i) => (
              <Reveal key={t.id} delay={i * 100} className="testimonial">
                <Quote size={28} className="text-[#8cc63f]" />
                <p className="mt-4 text-[15px] leading-7 text-[#4d4d4f] italic">{t.quote}</p>
                <div className="mt-5 flex items-center gap-3">
                  <span className="w-11 h-11 rounded-full bg-[#7ab929]/15 text-[#5f9a17] font-bold flex items-center justify-center">{t.name.charAt(0)}</span>
                  <div>
                    <div className="font-bold text-[#231f20] text-[14px]">{t.name}</div>
                    <div className="text-[12.5px] text-[#6d6e71]">{t.role}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section bg-white" id="faq">
        <div className="container-dic max-w-4xl">
          <Reveal><SectionTitle>Frequently Asked Questions</SectionTitle></Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-b border-[#e6e6e6]">
                  <AccordionTrigger className="text-left text-[16px] font-bold text-[#231f20] hover:text-[#7ab929] hover:no-underline py-5" data-testid={`faq-trigger-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[14.5px] leading-7 text-[#58595b] text-justify">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Connect - solid green section */}
      <section className="section bg-[#8cc63f]" id="connect" data-testid="connect-section">
        <div className="container-dic">
          <Reveal><SectionTitle>Talk to Our Team</SectionTitle></Reveal>
          <Reveal delay={100}><ContactForm variant="green" source="home" /></Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
